import React, {useCallback, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import moment from "moment";
import Box from "@mui/material/Box";
import PageTitle from "../../../shared/ui/PageTitle";
import CustomButton from "../../../shared/ui/CustomButton";
import Icon from "../../../shared/ui/Icon";
import BoxContent from "../../../shared/ui/BoxContent";
import CustomDataTable from "../../../shared/tables/CustomDataTable";
import StyledLink from "../../../shared/ui/LinkStyled";
import GenericButton from "../../../shared/ui/GenericButton";
import {useAuth} from "../../../hooks/useAuth";
import {APIGetWorkPlans} from "../api/workPlan";
import {getTableColumns} from "../components/_tableColumns";
import useDataTable from "../../../hooks/useDataTable";

interface IWorkPlan {
    work_plan_id: number;
    teacher_name: string;
    teacher_id: number;
    classroom_name: string;
    classroom_id: number;
    subject_name: string;
    grade_name: string;
    grade_id: number;
    subcategory_name: string;
    subcategory_id: number;
    work_plan_created_at: string;
}

const WorkPlans = () => {
    const {user, update} = useAuth();
    const navigate = useNavigate();

    const {
        loading,
        setLoading,
        tableData,
        setTableData,
        tableColumns,
        setTableColumns,
        totalRows,
        setTotalRows,
        perPage,
        setPerPage,
        page,
        query,
        submittedQuery,
        orderBy,
        order,
        handleChangePage,
        handleRequestSort,
        handleSearch,
        handleSearchChange,
        handleClearSearch,
    } = useDataTable("", "work_plan_id", undefined, "");

    const mapWorkPlansRow = useCallback((workPlan: IWorkPlan) => {

        const isPrincipal = user.position === "principal";

        const actions = (
            <Box display="flex" justifyContent="end">
                <Box>
                    <GenericButton
                        type="items"
                        onClick={() => navigate(`/plan-lucru/${workPlan.work_plan_id}`,
                            {state:
                                    {
                                        classroomId: workPlan.classroom_id,
                                        classroomName: workPlan.classroom_name,
                                        categoryName: workPlan.subcategory_name,
                                        categoryId: workPlan.subcategory_id,
                                        subjectName: workPlan.subject_name,
                                        grade: workPlan.grade_name,
                                        gradeId: workPlan.grade_id,
                                    }})}
                    />
                </Box>
            </Box>
        );

        return {
            work_plan_id: workPlan.work_plan_id,
            ...(isPrincipal && {
                teacher_name: <StyledLink to={`/profesori/${workPlan.teacher_id}`}>{workPlan.teacher_name}</StyledLink>,
            }),
            classroom_name:  <StyledLink to={`/clase/${workPlan.classroom_id}`}>{workPlan.classroom_name}</StyledLink>,
            subject_name: workPlan.subject_name,
            grade_name: workPlan.grade_name,
            subcategory_name: workPlan.subcategory_name,
            created_at: moment(workPlan.work_plan_created_at).locale("ro").format("DD.MM.YYYY"),
            actions,
        }
    }, [user.position, navigate])

    useEffect(() => {
        setLoading(true);
        APIGetWorkPlans({
            page: `?page=${page + 1}`,
            teacher_id: user.id,
            sort: orderBy,
            order: order,
            search: submittedQuery
        })
            .then((response) => {
                if (response.data.success) {
                    // console.log("APIGetReports OK", response);
                    setTableData(response.data.data.data.map((test: any) => mapWorkPlansRow(test)));
                    setTotalRows(response.data.data.total);
                    setPerPage(response.data.data.per_page);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch((err) => {
                // console.log("getClassroom ERR", err);
                update("error", {show: true, code: "A001"});
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, order, orderBy, submittedQuery]);

    useEffect(() => {
        const columns = getTableColumns(user.position, "workPlans");
        setTableColumns(columns);
    }, [user.position]);

    return (
        <Box component="main">
            <PageTitle
                actions={
                    <>
                        {user.position === "teacher" ? (
                            <CustomButton
                                buttonType="textIcon"
                                onClick={() => {
                                    navigate("/plan-lucru/adauga");
                                }}
                                icon={<Icon icon="fas plus" fixedWidth={true}/>}
                            >Generează plan de lucru</CustomButton>
                        ) : (
                            ""
                        )}
                    </>
                }
            >
                Planuri de lucru
            </PageTitle>
            <BoxContent>
                <CustomDataTable
                    loading={loading}
                    columns={tableColumns}
                    rows={tableData}
                    rowsPerPage={perPage}
                    page={page}
                    totalRows={totalRows}
                    onPageChange={handleChangePage}
                    handleRequestSort={handleRequestSort}
                    orderBy={orderBy}
                    order={order}
                    hasSearch={true}
                    onSearch={handleSearch}
                    onChange={handleSearchChange}
                    searchValue={query}
                    onClearSearch={handleClearSearch}
                />
            </BoxContent>
        </Box>
    )
}

export default WorkPlans;